import { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play, XCircle, RotateCcw, Trash2, Calendar, ArrowRight, ArrowLeft, Plus,
  LinkIcon, Pencil, Tag, Users, History,
} from 'lucide-react';

import { StatusBadge } from '@/components/shared/StatusBadge';
import { PriorityBadge } from '@/components/shared/PriorityBadge';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { StarRating } from '@/components/shared/StarRating';
import { EmptyState } from '@/components/shared/EmptyState';
import { CursorPagination } from '@/components/shared/CursorPagination';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  goalService,
  type GoalTask, type Evaluation,
} from '@/services/goal.service';
import { api } from '@/services/api';
import { cn } from '@/lib/utils';

// Helper: progress bar color
function progressBarColor(v: number) {
  if (v >= 100) return 'bg-green-500';
  if (v > 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ===========================================================================
// Goal Detail Page — FIX 5: Single scrollable layout, no tabs
// ===========================================================================
export function GoalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTab = (location.state as any)?.fromTab || 'goals-list';
  const backUrl = `/work?tab=${backTab}`;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: goal, isLoading } = useQuery({
    queryKey: ['goal', id],
    queryFn: () => goalService.get(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  const [activateOpen, setActivateOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dueDateHistoryOpen, setDueDateHistoryOpen] = useState(false);
  const [transitionLoading, setTransitionLoading] = useState(false);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editPriority, setEditPriority] = useState('medium');
  const [editDueDate, setEditDueDate] = useState('');
  const [editDueDateReason, setEditDueDateReason] = useState('');

  // Due date history
  const { data: dueDateHistory } = useQuery({
    queryKey: ['goal-due-date-history', id],
    queryFn: () => goalService.getDueDateHistory(id!).then((r) => r.data.data),
    enabled: !!id,
  });

  function invalidateGoal() {
    queryClient.invalidateQueries({ queryKey: ['goal', id] });
    queryClient.invalidateQueries({ queryKey: ['group-goals'] });
  }

  const activateMutation = useMutation({
    mutationFn: () => goalService.activate(id!),
    onSuccess: () => { invalidateGoal(); toast({ title: 'Goal activated' }); setActivateOpen(false); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Activation failed', variant: 'destructive' }),
    onSettled: () => setTransitionLoading(false),
  });
  const cancelMutation = useMutation({
    mutationFn: () => goalService.cancel(id!),
    onSuccess: () => { invalidateGoal(); toast({ title: 'Goal cancelled' }); setCancelOpen(false); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Cancel failed', variant: 'destructive' }),
    onSettled: () => setTransitionLoading(false),
  });
  const reopenMutation = useMutation({
    mutationFn: () => goalService.reopen(id!),
    onSuccess: () => { invalidateGoal(); toast({ title: 'Goal reopened as draft' }); setReopenOpen(false); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Reopen failed', variant: 'destructive' }),
    onSettled: () => setTransitionLoading(false),
  });
  const deleteMutation = useMutation({
    mutationFn: () => goalService.delete(id!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['groups'] }); toast({ title: 'Goal deleted' }); navigate(backUrl); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Delete failed', variant: 'destructive' }),
    onSettled: () => setTransitionLoading(false),
  });

  const editMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      if (editTitle !== goal?.title) payload.title = editTitle;
      if (editDesc !== (goal?.description ?? '')) payload.description = editDesc || null;
      if (editPriority !== goal?.priority) payload.priority = editPriority;
      if (editDueDate !== goal?.due_date) {
        payload.due_date = editDueDate;
        if (editDueDateReason) payload.due_date_change_reason = editDueDateReason;
      }
      return goalService.update(id!, payload as any);
    },
    onSuccess: () => {
      invalidateGoal();
      queryClient.invalidateQueries({ queryKey: ['goal-due-date-history', id] });
      toast({ title: 'Goal updated' });
      setEditOpen(false);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Update failed', variant: 'destructive' }),
  });

  function openEdit() {
    if (!goal) return;
    setEditTitle(goal.title);
    setEditDesc(goal.description ?? '');
    setEditPriority(goal.priority);
    setEditDueDate(goal.due_date ?? '');
    setEditDueDateReason('');
    setEditOpen(true);
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-3 w-full max-w-md" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mt-6"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
      </div>
    );
  }

  if (!goal) {
    return <div><EmptyState title="Goal not found" description="This goal may have been deleted." /></div>;
  }

  const progress = goal.progress_percentage ?? 0;
  const isOverdue = goal.status === 'active' && goal.due_date && new Date(goal.due_date) < new Date();
  const perms = goal.current_user_permissions ?? { can_edit: false, can_delete: false, can_change_status: false, can_manage_contributors: false, can_create_tasks: false, can_manage_dependencies: false };

  return (
    <div className="space-y-6">
      {/* Top bar: Back + Actions */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(backUrl)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Goals
        </button>
        <div className="flex items-center gap-2">
          {perms.can_change_status && goal.status === 'draft' && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setActivateOpen(true)}>
              <Play className="mr-1 h-4 w-4" /> Activate
            </Button>
          )}
          {perms.can_change_status && goal.status === 'active' && (
            <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)}>
              <XCircle className="mr-1 h-4 w-4" /> Cancel
            </Button>
          )}
          {perms.can_change_status && goal.status === 'cancelled' && (
            <Button size="sm" variant="outline" onClick={() => setReopenOpen(true)}>
              <RotateCcw className="mr-1 h-4 w-4" /> Reopen
            </Button>
          )}
          {perms.can_edit && (
            <Button size="sm" variant="outline" onClick={openEdit}>
              <Pencil className="mr-1 h-4 w-4" /> Edit
            </Button>
          )}
          {perms.can_delete && (
            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-1 h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Header Card */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{goal.title}</h1>
              <span className="font-mono text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">{goal.reference_id}</span>
            </div>
            {goal.group && (
              <Link to={`/groups/${goal.group.id}`} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-purple-600 mt-2 w-fit">
                <Users className="h-4 w-4" />
                {goal.group.name}
              </Link>
            )}
            {goal.description && (
              <div className="mt-3">
                <h3 className="font-semibold mb-1">Description</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{goal.description}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusBadge status={goal.status} />
            <PriorityBadge priority={goal.priority} />
          </div>
        </div>
      </Card>

      {/* Progress Card */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm font-semibold text-gray-900">{progress}%</span>
        </div>
        <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
          <span>{goal.completed_tasks ?? 0} of {goal.total_tasks ?? 0} tasks completed</span>
        </div>
        <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
          <div className={cn('h-full rounded-full transition-all', progressBarColor(progress))} style={{ width: `${progress}%` }} />
        </div>
      </Card>

      {/* Details Row */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
          <div>
            <p className="text-xs font-medium text-gray-400 mb-1">Owner</p>
            {goal.owner ? (
              <div className="flex items-center gap-2">
                <UserAvatar name={goal.owner.full_name} avatarUrl={goal.owner.avatar_url} size="sm" />
                <span className="text-sm font-medium text-gray-900">{goal.owner.full_name}</span>
              </div>
            ) : <span className="text-sm text-gray-400">—</span>}
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <p className="text-xs font-medium text-gray-400">Due Date</p>
              {dueDateHistory && dueDateHistory.length > 0 && (
                <button onClick={() => setDueDateHistoryOpen(true)} className="text-muted-foreground hover:text-foreground">
                  <History className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className={cn('text-sm', isOverdue ? 'text-destructive font-medium' : 'text-gray-900')}>
                {goal.due_date ? new Date(goal.due_date).toLocaleDateString() : '—'}
                {isOverdue && ' (Overdue)'}
              </span>
            </div>
          </div>
          {goal.activated_at && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1">Activated</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">{new Date(goal.activated_at).toLocaleDateString()}</span>
              </div>
            </div>
          )}
          {goal.completed_at && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1">Completed</p>
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-900">{new Date(goal.completed_at).toLocaleDateString()}</span>
              </div>
            </div>
          )}
          {goal.evaluation_score != null && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-1">Evaluation Score</p>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{goal.evaluation_score.toFixed(1)} / 5</span>
                <StarRating value={Math.round(goal.evaluation_score)} size={14} />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Tags Section */}
      {goal.tags && goal.tags.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900">Tags</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {goal.tags.map((tag) => (
              <span key={tag.id} className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium" style={{ backgroundColor: tag.color + '20', color: tag.color }}>
                {tag.name}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Contributors Section */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900">Contributors</h2>
          </div>
          {perms.can_manage_contributors && <Button size="sm" variant="outline" className="text-xs">Manage</Button>}
        </div>
        {(!goal.contributors || goal.contributors.length === 0) ? (
          <p className="text-sm text-gray-400">No contributors assigned</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {goal.contributors.map((u) => (
              <div key={u.id} className="flex items-center gap-1.5 rounded-full border px-3 py-1">
                <UserAvatar name={u.full_name} avatarUrl={u.avatar_url} size="xs" />
                <span className="text-xs font-medium">{u.full_name}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Evaluators Section */}
      {goal.evaluators && goal.evaluators.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-900">Evaluators</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {goal.evaluators.map((u) => (
              <div key={u.id} className="flex items-center gap-1.5 rounded-full border px-3 py-1">
                <UserAvatar name={u.full_name} avatarUrl={u.avatar_url} size="xs" />
                <span className="text-xs font-medium">{u.full_name}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Tasks Section */}
      <TasksSection goalId={id!} groupId={goal.group_id} totalTasks={goal.total_tasks} canCreate={perms.can_create_tasks} />

      {/* Dependencies Section */}
      <DependenciesSection goalId={id!} dependencies={goal.dependencies ?? []} dependents={goal.dependents ?? []} canManage={perms.can_manage_dependencies} />

      {/* Evaluations Section */}
      <EvaluationsSection goalId={id!} goalStatus={goal.status} />

      {/* Confirm Dialogs */}
      <ConfirmDialog open={activateOpen} onOpenChange={setActivateOpen} title="Activate Goal" description={`Activate "${goal.title}"? This will move the goal from Draft to Active.`} confirmLabel="Activate" isLoading={transitionLoading} onConfirm={() => { setTransitionLoading(true); activateMutation.mutate(); }} />
      <ConfirmDialog open={cancelOpen} onOpenChange={setCancelOpen} title="Cancel Goal" description={`Cancel "${goal.title}"? You can reopen it later.`} confirmLabel="Cancel Goal" variant="destructive" isLoading={transitionLoading} onConfirm={() => { setTransitionLoading(true); cancelMutation.mutate(); }} />
      <ConfirmDialog open={reopenOpen} onOpenChange={setReopenOpen} title="Reopen Goal" description={`Reopen "${goal.title}"? It will return to Draft status.`} confirmLabel="Reopen" isLoading={transitionLoading} onConfirm={() => { setTransitionLoading(true); reopenMutation.mutate(); }} />
      <ConfirmDialog open={deleteOpen} onOpenChange={setDeleteOpen} title="Delete Goal" description={`Permanently delete "${goal.title}"? All linked tasks will also be deleted.`} confirmLabel="Delete" variant="destructive" isLoading={transitionLoading} onConfirm={() => { setTransitionLoading(true); deleteMutation.mutate(); }} />

      {/* Edit Goal Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Edit Goal</DialogTitle>
            <DialogDescription>Update goal details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); editMutation.mutate(); }} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={editPriority} onValueChange={setEditPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['low', 'medium', 'high', 'critical'].map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Due Date</Label>
                <Input type="date" value={editDueDate} onChange={(e) => setEditDueDate(e.target.value)} />
              </div>
            </div>
            {editDueDate !== goal.due_date && (
              <div className="space-y-1.5">
                <Label>Reason for due date change</Label>
                <Input value={editDueDateReason} onChange={(e) => setEditDueDateReason(e.target.value)} placeholder="Optional reason..." />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={editMutation.isPending}>{editMutation.isPending ? 'Saving...' : 'Save Changes'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Due Date History Dialog */}
      <Dialog open={dueDateHistoryOpen} onOpenChange={setDueDateHistoryOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Due Date History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {dueDateHistory && dueDateHistory.length > 0 ? (
              dueDateHistory.map((entry: any) => (
                <div key={entry.id} className="p-3 rounded-lg border bg-muted/50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <UserAvatar name={entry.changed_by?.full_name ?? 'Unknown'} size="xs" />
                      <span className="font-medium text-sm">{entry.changed_by?.full_name ?? 'Unknown'}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{entry.old_date ? new Date(entry.old_date).toLocaleDateString() : '—'}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-medium">{entry.new_date ? new Date(entry.new_date).toLocaleDateString() : '—'}</span>
                  </div>
                  {entry.reason && <p className="text-sm text-muted-foreground mt-1">{entry.reason}</p>}
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No due date changes recorded.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===========================================================================
// Tasks Section
// ===========================================================================
function TasksSection({ goalId, groupId, totalTasks, canCreate }: { goalId: string; groupId: string; totalTasks: number; canCreate: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [cursor, setCursor] = useState<string | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');

  const { data, isLoading } = useQuery({
    queryKey: ['goal-tasks', goalId, cursor],
    queryFn: () => goalService.listTasks(goalId, { cursor, limit: 20 }).then((r) => r.data),
  });

  const tasks: GoalTask[] = data?.data ?? [];
  const pagination = data?.pagination;

  const createMutation = useMutation({
    mutationFn: () => api.post('/tasks', { title: taskTitle, priority: taskPriority, task_type: 'goal', goal_id: goalId, group_id: groupId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goal-tasks', goalId] });
      queryClient.invalidateQueries({ queryKey: ['goal', goalId] });
      toast({ title: 'Task created' }); setCreateOpen(false); setTaskTitle(''); setTaskPriority('medium');
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Create failed', variant: 'destructive' }),
    onSettled: () => setCreateLoading(false),
  });

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Tasks</h2>
          <p className="text-xs text-gray-400 mt-0.5">{totalTasks} task{totalTasks !== 1 ? 's' : ''} linked to this goal</p>
        </div>
        {canCreate && (
          <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Create Task
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : tasks.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No tasks yet. Create the first task for this goal.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-gray-400">
                <th className="py-2 pr-4 font-medium">Title</th>
                <th className="py-2 pr-4 font-medium">Assignee</th>
                <th className="py-2 pr-4 font-medium">Priority</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Hours</th>
                <th className="py-2 font-medium">Due Date</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2.5 pr-4">
                    <Link to={`/tasks/${task.id}`} className="text-sm font-medium text-gray-900 hover:text-purple-600">
                      {task.title}
                    </Link>
                    <span className="ml-2 font-mono text-[10px] text-gray-400">{task.reference_id}</span>
                  </td>
                  <td className="py-2.5 pr-4">
                    {task.assignee ? (
                      <div className="flex items-center gap-1.5">
                        <UserAvatar name={task.assignee.full_name} avatarUrl={task.assignee.avatar_url} size="xs" />
                        <span className="text-xs text-gray-600">{task.assignee.full_name}</span>
                      </div>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>
                  <td className="py-2.5 pr-4"><PriorityBadge priority={task.priority} /></td>
                  <td className="py-2.5 pr-4"><StatusBadge status={task.phase_status} /></td>
                  <td className="py-2.5 pr-4 text-xs text-gray-500">
                    {task.actual_hours > 0 ? `${task.actual_hours}h` : '—'}
                    {task.estimated_hours ? ` / ${task.estimated_hours}h` : ''}
                  </td>
                  <td className="py-2.5 text-xs text-gray-500">
                    {task.end_date ? new Date(task.end_date).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && (
        <div className="mt-3">
          <CursorPagination hasNext={pagination.has_more} hasPrev={false} onNext={() => setCursor(pagination.cursor ?? undefined)} onPrev={() => {}} />
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Create Goal Task</DialogTitle><DialogDescription>Add a task linked to this goal.</DialogDescription></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); setCreateLoading(true); createMutation.mutate(); }} className="space-y-4">
            <div className="space-y-2"><Label htmlFor="ct-title">Title *</Label><Input id="ct-title" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Task title" required /></div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={taskPriority} onValueChange={setTaskPriority}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['low', 'medium', 'high', 'critical'].map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}</SelectContent></Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand-primary hover:bg-brand-primary-dark" disabled={createLoading || !taskTitle.trim()}>{createLoading ? 'Creating...' : 'Create Task'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ===========================================================================
// Dependencies Section
// ===========================================================================
function DependenciesSection({ goalId, dependencies, dependents, canManage }: { goalId: string; dependencies: any[]; dependents: any[]; canManage: boolean }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [depGoalId, setDepGoalId] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const addMutation = useMutation({
    mutationFn: () => goalService.addDependency(goalId, depGoalId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goal', goalId] }); toast({ title: 'Dependency added' }); setAddOpen(false); setDepGoalId(''); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed to add dependency', variant: 'destructive' }),
    onSettled: () => setAddLoading(false),
  });
  const removeMutation = useMutation({
    mutationFn: (depId: string) => goalService.removeDependency(goalId, depId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goal', goalId] }); toast({ title: 'Dependency removed' }); },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Remove failed', variant: 'destructive' }),
  });

  if (dependencies.length === 0 && dependents.length === 0) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Dependencies</h2>
          {canManage && <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4" /> Add</Button>}
        </div>
        <p className="text-sm text-gray-400">No dependencies configured.</p>
        <AddDepDialog open={addOpen} onOpenChange={setAddOpen} depGoalId={depGoalId} setDepGoalId={setDepGoalId} addLoading={addLoading} onSubmit={() => { setAddLoading(true); addMutation.mutate(); }} />
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Dependencies</h2>
        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}><Plus className="mr-1 h-4 w-4" /> Add</Button>
      </div>
      {dependencies.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-gray-400 mb-2">Depends On</h3>
          <div className="space-y-2">
            {dependencies.map((dep) => (
              <div key={dep.id} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <LinkIcon className="h-4 w-4 text-gray-400 shrink-0" />
                  {dep.depends_on ? (
                    <>
                      <span className="font-mono text-xs text-gray-400">{dep.depends_on.reference_id}</span>
                      <Link to={`/goals/${dep.depends_on.id}`} className="truncate text-sm font-medium hover:text-purple-600">{dep.depends_on.title}</Link>
                      <StatusBadge status={dep.depends_on.status} />
                    </>
                  ) : <span className="text-sm text-gray-400">Unknown goal</span>}
                </div>
                {canManage && <Button variant="ghost" size="sm" className="text-xs text-destructive hover:text-destructive shrink-0" onClick={() => removeMutation.mutate(dep.id)}>Remove</Button>}
              </div>
            ))}
          </div>
        </div>
      )}
      {dependents.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-gray-400 mb-2">Depended By</h3>
          <div className="space-y-2">
            {dependents.map((dep) => (
              <div key={dep.id} className="flex items-center gap-2 rounded-lg border p-3">
                <ArrowRight className="h-4 w-4 text-gray-400 shrink-0" />
                {dep.entity ? (
                  <>
                    <span className="font-mono text-xs text-gray-400">{dep.entity.reference_id}</span>
                    <Link to={`/goals/${dep.entity.id}`} className="truncate text-sm font-medium hover:text-purple-600">{dep.entity.title}</Link>
                    <StatusBadge status={dep.entity.status} />
                  </>
                ) : <span className="text-sm text-gray-400">Unknown goal</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <AddDepDialog open={addOpen} onOpenChange={setAddOpen} depGoalId={depGoalId} setDepGoalId={setDepGoalId} addLoading={addLoading} onSubmit={() => { setAddLoading(true); addMutation.mutate(); }} />
    </Card>
  );
}

function AddDepDialog({ open, onOpenChange, depGoalId, setDepGoalId, addLoading, onSubmit }: {
  open: boolean; onOpenChange: (v: boolean) => void; depGoalId: string; setDepGoalId: (v: string) => void; addLoading: boolean; onSubmit: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader><DialogTitle>Add Dependency</DialogTitle><DialogDescription>Enter the ID of the goal this goal depends on.</DialogDescription></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">
          <div className="space-y-2"><Label htmlFor="dep-id">Goal ID *</Label><Input id="dep-id" value={depGoalId} onChange={(e) => setDepGoalId(e.target.value)} placeholder="Paste goal UUID" required /></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-brand-primary hover:bg-brand-primary-dark" disabled={addLoading || !depGoalId.trim()}>{addLoading ? 'Adding...' : 'Add'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Evaluations Section
// ===========================================================================
function EvaluationsSection({ goalId, goalStatus }: { goalId: string; goalStatus: string }) {
  const isCompleted = goalStatus === 'completed' || goalStatus === 'completed_late';

  const { data, isLoading } = useQuery({
    queryKey: ['goal-evaluations', goalId],
    queryFn: () => goalService.getEvaluations(goalId).then((r) => r.data.data),
    enabled: isCompleted,
  });

  if (!isCompleted) return null;

  const evaluations: Evaluation[] = data ?? [];

  if (isLoading) {
    return <Card className="p-6"><Skeleton className="h-20" /></Card>;
  }

  if (evaluations.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Evaluations</h2>
        <p className="text-sm text-gray-400">No evaluations yet. Assigned evaluators haven't submitted their reviews.</p>
      </Card>
    );
  }

  const avgScore = evaluations.reduce((s, e) => s + e.score, 0) / evaluations.length;

  return (
    <Card className="space-y-4">
      <h2 className="text-sm font-semibold text-gray-900">Evaluations</h2>

      <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
        <StarRating value={Math.round(avgScore)} />
        <span className="text-lg font-bold">{avgScore.toFixed(1)}</span>
        <span className="text-sm text-gray-500">({evaluations.length} evaluation{evaluations.length !== 1 ? 's' : ''})</span>
      </div>

      <div className="space-y-2">
        {evaluations.map((ev) => (
          <div key={ev.id} className="rounded-lg border p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <UserAvatar name={ev.evaluator?.full_name ?? 'Unknown'} avatarUrl={ev.evaluator?.avatar_url} size="sm" />
                <div>
                  <p className="text-sm font-medium">{ev.evaluator?.full_name ?? '—'}</p>
                  <p className="text-xs text-gray-400">{new Date(ev.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <StarRating value={ev.score} size={18} />
            </div>
            {ev.comment && <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{ev.comment}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}
